import { create } from 'zustand';
import type { ChannelKind, MessageDTO, MessageTargetRef } from '@clanfire/shared';

export type SettingsSection = 'account' | 'profile' | 'blaze' | 'privacy' | 'security' | 'appearance' | 'voice' | 'notifications' | 'staff';
export type ServerSettingsSection = 'overview' | 'rules' | 'safety' | 'roles' | 'emoji' | 'soundboard' | 'automod' | 'members' | 'invites' | 'bans' | 'audit';

export type Modal =
  | { type: 'createServer' }
  | { type: 'joinServer'; code?: string }
  | { type: 'invite'; serverId: string }
  | { type: 'serverSettings'; serverId: string; section?: ServerSettingsSection }
  | { type: 'createChannel'; serverId: string; channelType: ChannelKind; categoryId?: string | null }
  | { type: 'category'; serverId: string; categoryId?: string }
  | { type: 'channelSettings'; channelId: string }
  | { type: 'userSettings'; section?: SettingsSection }
  | { type: 'profile'; userId: string; serverId?: string }
  /** Message friends; from a conversation, start a group with its people or add to the group. */
  | { type: 'newDm'; conversationId?: string }
  | { type: 'quickSwitcher' }
  | { type: 'shortcuts' }
  | { type: 'customStatus' }
  | { type: 'nickname'; serverId: string; userId: string }
  /** Your avatar, banner and bio for one server (Blaze). */
  | { type: 'serverProfile'; serverId: string }
  | { type: 'timeout'; serverId: string; userId: string }
  | { type: 'createPoll'; target: MessageTargetRef }
  /** On a message, or (without one) a new thread in the channel. */
  | { type: 'createThread'; channelId: string; message?: MessageDTO }
  /** Report a message, or an account when there's no message. `subject` finishes "Report …". */
  | { type: 'report'; messageId?: string; userId?: string; subject: string }
  /** A server's events; `eventId` opens one, `create` the form for a new one. */
  | { type: 'events'; serverId: string; eventId?: string; create?: boolean }
  /** Save a message with a reminder. */
  | { type: 'remindMe'; message: MessageDTO }
  /** What a message said before it was edited. */
  | { type: 'editHistory'; message: MessageDTO }
  /** The short welcome for new accounts (also replayable from Settings). */
  | { type: 'welcomeTour' }
  /** Bug reports and ideas, straight to the team. */
  | { type: 'feedback' }
  /** Roles a server lets members give themselves. */
  | { type: 'pickRoles'; serverId: string }
  /** Forward a message to a DM or channel. */
  | { type: 'forward'; message: MessageDTO };

interface UiState {
  modal: Modal | null;
  openModal: (modal: Modal) => void;
  closeModal: () => void;

  /** Mobile navigation drawer (server rail + channels). */
  navOpen: boolean;
  setNavOpen: (open: boolean) => void;
  /** Member list: a column on desktop, an overlay on small screens. */
  membersOpen: boolean;
  setMembersOpen: (open: boolean) => void;
  searchOpen: boolean;
  setSearchOpen: (open: boolean) => void;

  replyTo: Record<string, MessageDTO | null>;
  setReplyTo: (key: string, message: MessageDTO | null) => void;
  editing: Record<string, string | null>;
  setEditing: (key: string, messageId: string | null) => void;

  /** A request to scroll a message into view (search results, reply clicks). */
  jumpTo: { key: string; messageId: string; nonce: number } | null;
  requestJump: (key: string, messageId: string) => void;
  clearJump: () => void;

  lastChannelByServer: Record<string, string>;
  rememberChannel: (serverId: string, channelId: string) => void;
  reset: () => void;
}

const LAST_CHANNEL_KEY = 'clanfire:lastChannels';
function loadLastChannels(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(LAST_CHANNEL_KEY) ?? '{}') as Record<string, string>;
  } catch {
    return {};
  }
}

export const useUi = create<UiState>((set) => ({
  modal: null,
  openModal: (modal) => set({ modal }),
  closeModal: () => set({ modal: null }),

  navOpen: false,
  setNavOpen: (navOpen) => set({ navOpen }),
  membersOpen: typeof window !== 'undefined' ? window.innerWidth >= 1200 : true,
  setMembersOpen: (membersOpen) => set({ membersOpen, ...(membersOpen ? { searchOpen: false } : {}) }),
  searchOpen: false,
  setSearchOpen: (searchOpen) => set({ searchOpen }),

  replyTo: {},
  setReplyTo: (key, message) => set((s) => ({ replyTo: { ...s.replyTo, [key]: message }, editing: { ...s.editing, [key]: null } })),
  editing: {},
  setEditing: (key, messageId) => set((s) => ({ editing: { ...s.editing, [key]: messageId } })),

  jumpTo: null,
  requestJump: (key, messageId) => set({ jumpTo: { key, messageId, nonce: Date.now() } }),
  clearJump: () => set({ jumpTo: null }),

  lastChannelByServer: loadLastChannels(),
  rememberChannel: (serverId, channelId) =>
    set((s) => {
      if (s.lastChannelByServer[serverId] === channelId) return s;
      const lastChannelByServer = { ...s.lastChannelByServer, [serverId]: channelId };
      try {
        localStorage.setItem(LAST_CHANNEL_KEY, JSON.stringify(lastChannelByServer));
      } catch {
        /* ignore */
      }
      return { lastChannelByServer };
    }),

  reset: () => set({ modal: null, navOpen: false, searchOpen: false, replyTo: {}, editing: {}, jumpTo: null }),
}));

/* ------------------------------------------------------------------ */
/* Toasts                                                              */
/* ------------------------------------------------------------------ */

export interface Toast {
  id: number;
  kind: 'success' | 'error' | 'info';
  message: string;
}

interface ToastState {
  toasts: Toast[];
  push: (kind: Toast['kind'], message: string) => void;
  dismiss: (id: number) => void;
}

let toastId = 0;
export const useToasts = create<ToastState>((set, get) => ({
  toasts: [],
  push: (kind, message) => {
    const id = ++toastId;
    set((s) => ({ toasts: [...s.toasts.filter((t) => t.message !== message).slice(-3), { id, kind, message }] }));
    setTimeout(() => get().dismiss(id), kind === 'error' ? 6000 : 3500);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export const toast = {
  success: (message: string) => useToasts.getState().push('success', message),
  error: (message: string) => useToasts.getState().push('error', message),
  info: (message: string) => useToasts.getState().push('info', message),
};

/* ------------------------------------------------------------------ */
/* Confirmation dialogs                                                */
/* ------------------------------------------------------------------ */

export interface ConfirmOptions {
  title: string;
  body: string;
  confirmLabel?: string;
  danger?: boolean;
  /** Optional free-text input (e.g. a ban reason); its value is passed to the resolver. */
  input?: { label: string; placeholder?: string; type?: 'text' | 'password'; /** What it starts with. */ value?: string };
}

interface ConfirmState {
  request: (ConfirmOptions & { resolve: (result: { confirmed: boolean; value: string }) => void }) | null;
  open: (options: ConfirmOptions) => Promise<{ confirmed: boolean; value: string }>;
  close: (confirmed: boolean, value?: string) => void;
}

export const useConfirm = create<ConfirmState>((set, get) => ({
  request: null,
  open: (options) =>
    new Promise((resolve) => {
      get().request?.resolve({ confirmed: false, value: '' });
      set({ request: { ...options, resolve } });
    }),
  close: (confirmed, value = '') => {
    get().request?.resolve({ confirmed, value });
    set({ request: null });
  },
}));

/** `await confirm({...})` → true when the user confirmed. */
export async function confirm(options: ConfirmOptions): Promise<boolean> {
  return (await useConfirm.getState().open(options)).confirmed;
}
